import React, { useEffect, useState } from 'react';
import { PageContent } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import { VEHICLE_TYPE_EV, VEHICLE_TYPE_CATEGORY_IDS_NUM, VEHICLE_TYPES } from '../../../../constants/vehicleTypes';
import { trackGAEvent } from '../../utils/gaTracking';
import { ATTRIBUTE_VALUES } from '../../../../constants/attributes';
import getFavorites from '../Carousels/repositories/getFavorites';
import deleteFavorite from '../Carousels/repositories/deleteFavorite';
import createFavorite from '../Carousels/repositories/createFavorite';

import PageBackground from '../PageBackground/components/PageBackground';
import BannerContainer from '../Banners/Banners';
import FeedsApp from '../FeedsApp/FeedsApp';
import CarsSearchApp from '../CarsSearch/CarsSearch';
import RecentlyViewedList from '../Motors/components/RecentlyViewedList';
import ContentBlocks from './components/ContentBlocks';
import ContentArticles from '../ContentArticles/components/ContentArticles';
import FaqList from '../FaqList/FaqList';
import VehicleNavigationBar from '../Motors/components/VehicleNavigationBar';
import FavoritesList from '../Motors/components/FavoritesList';
import HubList from './components/HubList';

import Classes from './components/CarsPageContent.scss';
import { TFavoriteAd } from 'types/TFavoriteAds';
import { TCarouselListingItem } from 'types/TCarousels';
import { TEnvConfigCars } from 'types/TServerContext';
import BuyerInfoModal from '../FavouritesConnect/BuyerInfoModal';
type TCarsPageContentAppProps = {
  environment: TEnvConfigCars;
  appState: any;
};

const CarsPageContentApp = ({ environment, appState }: TCarsPageContentAppProps) => {
  const { t } = useI18nContext();
  const [vehicleType, setVehicleType] = useState(environment.pageVehicleType);
  const { isFavouritesConnectEnabled } = environment.experiments;
  const [favoriteItems, setFavoriteItems] = useState<TCarouselListingItem[]>([]);
  const [relevantFavoriteItems, setRelevantFavoriteItems] = useState<TCarouselListingItem[]>([]);
  const [showFavouriteInfoModal, setShowFavouriteInfoModal] = useState(false);
  const needsBasedContent = appState.contentArticles.cars.find(({ key }) => key === 'needs_based_search');
  const intent = environment.intent;
  const isLeasePage = intent === 'lease';

  useEffect(() => {
    window.addEventListener('vehicle-type-changed', (event) => {
      setVehicleType((event as CustomEvent).detail);
    });

    window.addEventListener('placead', (event) => {
      trackGAEvent('placeAdButtonClicked', 'header');
    });
  }, []);

  useEffect(() => {
    if (!environment.userLoggedIn) {
      return;
    }

    (async () => {
      let favoriteAds: TFavoriteAd[] = [];
      try {
        favoriteAds = await getFavorites();
      } catch {}

      if (!favoriteAds.length) {
        return;
      }

      setFavoriteItems(favoriteAds);
    })();
  }, [environment.userLoggedIn]);

  useEffect(() => {
    setRelevantFavoriteItems(
      favoriteItems.filter((fav) => {
        const matchesL2Category =
          vehicleType === VEHICLE_TYPES.CARS
            ? !Object.values(VEHICLE_TYPE_CATEGORY_IDS_NUM).includes(fav.l2CategoryId)
            : fav.l2CategoryId === VEHICLE_TYPE_CATEGORY_IDS_NUM[vehicleType];
        const matchesPriceType = !isLeasePage ? !fav.isPricePerMonth : fav.isPricePerMonth;
        return matchesL2Category && matchesPriceType;
      }),
    );
  }, [favoriteItems, vehicleType, isLeasePage]);

  const onFavClickHandler = async (item: TCarouselListingItem, isFavorite: boolean) => {
    if (isFavorite) {
      trackGAEvent('WatchlistRemoveBegin');
      setFavoriteItems(favoriteItems.filter((ad) => ad.itemId !== item.itemId));
      await deleteFavorite(item.itemId, environment.xsrfToken);
      trackGAEvent('WatchlistRemoveSuccess');
    } else {
      trackGAEvent('WatchlistAddBegin');
      setFavoriteItems([item, ...favoriteItems]);
      await createFavorite(item.itemId, environment.xsrfToken);
      trackGAEvent('WatchlistAddSuccess');

      setShowFavouriteInfoModal(true);
    }
  };

  return (
    <EnvironmentProvider value={environment}>
      <PageContent className="vehicle-nav-root">
        <VehicleNavigationBar environment={environment} />
      </PageContent>
      <section className="Page-section Page-section--full-width">
        <PageBackground />
        <PageContent className={Classes.intentContent}>
          <section className={Classes.searchSection}>
            <h1 className="hz-Title hz-Title--title4 sectionHeader Page-title">
              {t(`page.title.${intent}.${vehicleType}`)}
            </h1>
            <CarsSearchApp
              l1Category={appState.l1Category}
              carsSearchFields={appState.carsSearchFields}
              userPostcode={appState.userPostcode}
              vehicleType={vehicleType}
              priceType={intent === 'buy' ? ATTRIBUTE_VALUES.PRICE_TYPE.BUY : ATTRIBUTE_VALUES.PRICE_TYPE.LEASE}
            />
          </section>
          {vehicleType === VEHICLE_TYPE_EV ? (
            <section className="Page-section Page-section--full-width">
              <ContentArticles contentArticles={appState.contentArticles.evs} isLeasePage={isLeasePage} />
              <section className="faqSection PageContent-offset">
                <FaqList />
              </section>
            </section>
          ) : (
            <section className="Page-section Page-section--full-width">
              {Boolean(relevantFavoriteItems.length) && (
                <FavoritesList
                  title={t(`cars.carousel.title.user_favorites.${vehicleType}`)}
                  favoriteAdItems={relevantFavoriteItems}
                  onFavClickHandler={onFavClickHandler}
                />
              )}
              <RecentlyViewedList
                favoriteAdItems={relevantFavoriteItems}
                onFavClickHandler={onFavClickHandler}
                vehicleType={vehicleType}
                isLease={isLeasePage}
              />
              {vehicleType === VEHICLE_TYPES.CARS && <HubList content={needsBasedContent} isLeasePage={isLeasePage} />}
              {appState.withBanners && (
                <section className="Page-section Page-section--full-width bannerSection" id="banner-root">
                  <BannerContainer id="banner-top-dt" environment={environment} l1Category={appState.l1Category} />
                </section>
              )}
              <section className="Page-section" id="feeds-root">
                <FeedsApp
                  appState={appState}
                  environment={environment}
                  favoriteAdItems={relevantFavoriteItems}
                  onFavClickHandler={onFavClickHandler}
                  asCarousels
                  vehicleType={vehicleType}
                  isLease={isLeasePage}
                />
              </section>
              {appState.withContentBlocks && <ContentBlocks contentArticles={appState.contentArticles.cars} />}
            </section>
          )}
        </PageContent>
      </section>
      {isFavouritesConnectEnabled && showFavouriteInfoModal && (
        <BuyerInfoModal onClose={() => setShowFavouriteInfoModal(false)} tenant={environment.tenantContext.tenant} />
      )}
    </EnvironmentProvider>
  );
};

export default CarsPageContentApp;
